const CONTROLLER_PATTERN = /^[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}$/

/** Company id from admin bridge query or shared cookie. */
export function resolvePromotionCompanyId(routeQuery = {}, cookieCompanyId) {
  const fromQuery = typeof routeQuery.companyId === 'string' ? routeQuery.companyId.trim() : ''
  if (fromQuery) {
    return fromQuery
  }
  if (typeof cookieCompanyId === 'string' && cookieCompanyId.trim()) {
    return cookieCompanyId.trim()
  }
  return null
}

/** Parse /app?newDevice=1&controller=…&stockUnitId=…&companyId=… from route query. */
export function parseNewDeviceFromStockQuery(routeQuery = {}) {
  if (String(routeQuery.newDevice ?? '') !== '1') {
    return null
  }

  const controller =
    typeof routeQuery.controller === 'string' ? routeQuery.controller.trim().toUpperCase() : ''
  const stockUnitId =
    typeof routeQuery.stockUnitId === 'string' ? routeQuery.stockUnitId.trim() : ''

  if (!controller || !stockUnitId || !CONTROLLER_PATTERN.test(controller)) {
    return null
  }

  return { controller, stockUnitId }
}

const STOCK_PROMOTION_QUERY_KEYS = ['newDevice', 'controller', 'stockUnitId', 'companyId']

export { STOCK_PROMOTION_QUERY_KEYS }

export const PENDING_NEW_DEVICE_FROM_STOCK_KEY = 'ixPendingNewDeviceFromStock'

/** Params that must survive device-type redirects (see deviceTypeGuard). */
export const ROUTE_QUERY_KEYS_PRESERVED_ON_REDIRECT = [
  'email',
  'createCompanyToken',
  'serviceCompanyToken',
  'deviceServiceCompanyToken',
  'openAdmin',
  'inviteEmail',
  'tab',
  ...STOCK_PROMOTION_QUERY_KEYS,
]

export function loadPendingNewDeviceFromStock(routeQuery = {}) {
  const fromQuery = parseNewDeviceFromStockQuery(routeQuery)
  if (fromQuery) {
    return fromQuery
  }

  // Session backup only during an active admin bridge (?newDevice=1), e.g. after a redirect
  // that dropped controller/stockUnitId. Never read stale storage on normal /app visits.
  if (String(routeQuery?.newDevice ?? '') !== '1') {
    return null
  }

  try {
    const raw = sessionStorage.getItem(PENDING_NEW_DEVICE_FROM_STOCK_KEY)
    if (!raw) {
      return null
    }
    const payload = JSON.parse(raw)
    if (payload?.controller && payload?.stockUnitId) {
      return payload
    }
  } catch (error) {
    console.error('[newDeviceFromStock] load pending failed', error)
  }
  return null
}

export function clearPendingNewDeviceFromStock() {
  try {
    sessionStorage.removeItem(PENDING_NEW_DEVICE_FROM_STOCK_KEY)
  } catch (error) {
    console.error('[newDeviceFromStock] clear pending failed', error)
  }
}

export function persistPendingNewDeviceFromStock(payload) {
  if (!payload?.controller || !payload?.stockUnitId) {
    return
  }
  try {
    sessionStorage.setItem(PENDING_NEW_DEVICE_FROM_STOCK_KEY, JSON.stringify(payload))
  } catch (error) {
    console.error('[newDeviceFromStock] persist pending failed', error)
  }
}

/** Switch vuex company when admin bridge params target a company the user belongs to. */
export function applyPromotionCompanyFromBridge({
  routeQuery,
  cookieCompanyId,
  companies,
  currentCompanyId,
  setCompanyId,
}) {
  if (String(routeQuery?.newDevice ?? '') !== '1') {
    return { ok: true, skipped: true }
  }

  const targetCompanyId = resolvePromotionCompanyId(routeQuery, cookieCompanyId)
  if (!targetCompanyId) {
    return { ok: true, skipped: true }
  }

  const isMember = (companies ?? []).some((company) => company.id === targetCompanyId)
  if (!isMember) {
    return { ok: false, mismatch: true, targetCompanyId }
  }

  if (currentCompanyId !== targetCompanyId) {
    setCompanyId(targetCompanyId)
  }

  return { ok: true, targetCompanyId }
}

/** Remove new-device bridge params from the URL after handling. */
export function clearNewDeviceFromStockQuery(router, route) {
  if (!STOCK_PROMOTION_QUERY_KEYS.some((key) => route.query[key])) {
    return
  }
  const nextQuery = { ...route.query }
  for (const key of STOCK_PROMOTION_QUERY_KEYS) {
    delete nextQuery[key]
  }
  router.replace({ path: route.path, query: nextQuery })
}
